import axios from "axios";

const BASE_URL = import.meta.env.VITE_API_BASE_URL;

// Get auth token from localStorage
const getAuthToken = (): string | null => {
  return localStorage.getItem("access") || localStorage.getItem("token");
};

// Get auth headers
const getAuthHeaders = () => {
  const token = getAuthToken();
  return {
    "Content-Type": "application/json",
    ...(token && { Authorization: `Bearer ${token}` }),
  };
};

interface Store {
  id: number;
  name: string;
  latitude: number | string;
  longitude: number | string;
  coordinates?: string;
}

/**
 * Get store details by store ID
 * This endpoint might vary based on your backend API
 * @param storeId - Store ID
 */
export const getStoreById = async (storeId: number): Promise<Store | null> => {
  try {
    // Try different possible endpoints
    const endpoints = [
      `/stores/${storeId}/`,
      `/store/${storeId}/`,
      `/delivery/store/${storeId}/`,
    ];

    for (const endpoint of endpoints) {
      try {
        const response = await axios.get(`${BASE_URL}${endpoint}`, {
          headers: getAuthHeaders(),
        });
        
        // Handle different response structures
        const store = response.data?.data || response.data?.store || response.data;
        if (store) {
          return store;
        }
      } catch (err) {
        // Try next endpoint
        continue;
      }
    }

    return null;
  } catch (error) {
    console.error("Error fetching store:", error);
    return null;
  }
};

/**
 * Extract store coordinates from various possible formats
 * @param store - Store object or profile object
 * @returns Object with lat and lng, or null if not found
 */
export const extractStoreCoordinates = (store: any): { lat: number; lng: number } | null => {
  if (!store) return null;

  // Try different possible locations for coordinates
  const possiblePaths = [
    store.coordinates,
    store.store?.coordinates,
    store.partner?.store?.coordinates,
    store.latitude && store.longitude ? { lat: store.latitude, lng: store.longitude } : null,
    store.store?.latitude && store.store?.longitude 
      ? { lat: store.store.latitude, lng: store.store.longitude } 
      : null,
  ];

  for (const coords of possiblePaths) {
    if (!coords) continue;

    // Handle string format "lat,lng"
    if (typeof coords === 'string') {
      const [lat, lng] = coords.split(',').map(Number);
      if (!isNaN(lat) && !isNaN(lng)) {
        return { lat, lng };
      }
    }
    // Handle object format {lat, lng} or {latitude, longitude}
    else if (typeof coords === 'object') {
      const lat = coords.lat || coords.latitude;
      const lng = coords.lng || coords.longitude;
      if (lat !== undefined && lng !== undefined && !isNaN(Number(lat)) && !isNaN(Number(lng))) {
        return { lat: Number(lat), lng: Number(lng) };
      }
    }
  }

  return null;
};








